import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface Goal {
  id: number;
  title: string;
  data: Record<string, any>;
  created_at: string;
  updated_at: string;
}

const Goals: React.FC = () => {
  const navigate = useNavigate();
  const [goals, setGoals] = useState<Goal[]>([]);
  const [newGoalTitle, setNewGoalTitle] = useState('');
  const [editingGoal, setEditingGoal] = useState<Goal | null>(null);
  const [editTitle, setEditTitle] = useState('');

  useEffect(() => {
    fetchGoals();
  }, []);

  const fetchGoals = async () => {
    try {
      const response = await fetch('/api/goals');
      const data = await response.json();
      setGoals(data);
    } catch (error) {
      console.error('Error fetching goals:', error);
    }
  };

  const handleCreateGoal = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newGoalTitle.trim()) return;

    try {
      const response = await fetch('/api/goals', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          goal: {
            title: newGoalTitle,
            data: {},
          },
        }),
      });

      if (response.ok) {
        const newGoal = await response.json();
        setGoals([...goals, newGoal]);
        setNewGoalTitle('');
      } else {
        console.error('Error creating goal:', await response.json());
      }
    } catch (error) {
      console.error('Error creating goal:', error);
    }
  };

  const handleEditGoal = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingGoal || !editTitle.trim()) return;

    try {
      const response = await fetch(`/api/goals/${editingGoal.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          goal: {
            title: editTitle,
            data: editingGoal.data,
          },
        }),
      });

      if (response.ok) {
        const updatedGoal = await response.json();
        setGoals(goals.map((goal) => (goal.id === updatedGoal.id ? updatedGoal : goal)));
        setEditingGoal(null);
        setEditTitle('');
      } else {
        console.error('Error updating goal:', await response.json());
      }
    } catch (error) {
      console.error('Error updating goal:', error);
    }
  };

  const handleDeleteGoal = async (goalId: number) => {
    if (!confirm('Are you sure you want to delete this goal?')) return;

    try {
      const response = await fetch(`/api/goals/${goalId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        setGoals(goals.filter((goal) => goal.id !== goalId));
      } else {
        console.error('Error deleting goal:', await response.json());
      }
    } catch (error) {
      console.error('Error deleting goal:', error);
    }
  };

  const startEditing = (goal: Goal) => {
    setEditingGoal(goal);
    setEditTitle(goal.title);
  };

  const handleViewGoal = (goalId: number) => {
    navigate(`/goals/${goalId}`);
  };

  return (
    <div className="p-4">
      <h1 className="text-2xl font-bold mb-4">Goals</h1>

      <form onSubmit={handleCreateGoal} className="mb-6">
        <div className="flex gap-2">
          <input
            type="text"
            value={newGoalTitle}
            onChange={(e) => setNewGoalTitle(e.target.value)}
            placeholder="Enter goal title"
            className="flex-1 px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            type="submit"
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Add Goal
          </button>
        </div>
      </form>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {goals.map((goal) => (
          <div key={goal.id} className="p-4 border rounded-lg shadow-sm hover:shadow-md transition-shadow">
            {editingGoal?.id === goal.id ? (
              <form onSubmit={handleEditGoal} className="space-y-2">
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <div className="flex gap-2">
                  <button
                    type="submit"
                    className="px-3 py-1 bg-green-500 text-white rounded-md hover:bg-green-600"
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setEditingGoal(null);
                      setEditTitle('');
                    }}
                    className="px-3 py-1 bg-gray-500 text-white rounded-md hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <>
                <h2 className="text-xl font-semibold mb-2">{goal.title}</h2>
                <p className="text-sm text-gray-500 mb-2">
                  Created: {new Date(goal.created_at).toLocaleDateString()}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleViewGoal(goal.id)}
                    className="px-3 py-1 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                  >
                    View
                  </button>
                  <button
                    onClick={() => startEditing(goal)}
                    className="px-3 py-1 bg-yellow-500 text-white rounded-md hover:bg-yellow-600"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeleteGoal(goal.id)}
                    className="px-3 py-1 bg-red-500 text-white rounded-md hover:bg-red-600"
                  >
                    Delete
                  </button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Goals;
